import React, { useState, useEffect } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { Plus, Search, Calendar, Building2, X, ArrowUpCircle, ArrowDownCircle, Edit, Trash2, FileText, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import SearchableSelect from '@/components/SearchableSelect';
import ConfirmModal from '@/components/ConfirmModal';
import { searchWithoutAccents } from '@/utils/helpers';

const EntradasSaidas = () => {
  const [movimentacoes, setMovimentacoes] = useState([]);
  const [pessoas, setPessoas] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogCadastro, setDialogCadastro] = useState(false);
  const [editingMovimentacao, setEditingMovimentacao] = useState(null);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [orderBy, setOrderBy] = useState('data'); // data, nome, descricao, valor
  const [orderDir, setOrderDir] = useState('desc'); // asc, desc
  const [confirmDelete, setConfirmDelete] = useState({ isOpen: false, id: null });
  const [formCadastro, setFormCadastro] = useState({
    tipo: 'PF',
    nome_razao: '',
    cpf_cnpj: '',
    email: '',
    celular: '',
    cliente: false,
    fornecedor: false,
    motorista: false,
    funcionario: false
  });
  const [filtros, setFiltros] = useState({
    data_inicio: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    data_fim: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0],
    banco: '',
    tipo: '',
    forma_pagamento: ''
  });
  const [formData, setFormData] = useState({
    tipo: 'receber',
    pessoa_id: '',
    titulo: '',
    valor_original: '',
    banco: '',
    forma_pagamento: 'PIX',
    data: new Date().toISOString().split('T')[0],
    descricao: ''
  });

  useEffect(() => {
    loadMovimentacoes();
    loadPessoas();
    loadBancos();
  }, []);

  const loadBancos = async () => {
    try {
      const response = await api.get('/bancos');
      setBancos(response.data);
    } catch (error) {
      console.error('Erro ao carregar bancos');
    }
  };

  const loadPessoas = async () => {
    try {
      const response = await api.get('/pessoas');
      // Ordenar alfabeticamente
      const pessoasOrdenadas = response.data.sort((a, b) => 
        a.nome_razao.localeCompare(b.nome_razao)
      );
      setPessoas(pessoasOrdenadas);
    } catch (error) {
      console.error('Erro ao carregar pessoas');
    }
  };

  const loadMovimentacoes = async () => {
    try {
      const params = new URLSearchParams();
      if (filtros.banco) params.append('banco', filtros.banco);
      if (filtros.tipo) params.append('tipo', filtros.tipo);
      if (filtros.data_inicio) params.append('data_inicio', filtros.data_inicio);
      if (filtros.data_fim) params.append('data_fim', filtros.data_fim);
      
      const response = await api.get(`/financeiros?${params.toString()}`);
      const movs = response.data.filter(m => m.status === 'pago');
      
      // Buscar nomes das pessoas
      const pessoasResponse = await api.get('/pessoas');
      const pessoasMap = {};
      pessoasResponse.data.forEach(p => {
        pessoasMap[p.id] = p.nome_razao;
      });
      
      // Adicionar nome da pessoa em cada movimentação
      const movsComNome = movs.map(m => ({
        ...m,
        pessoa_nome: pessoasMap[m.pessoa_id] || 'Movimentação Manual'
      }));
      
      setMovimentacoes(movsComNome);
    } catch (error) {
      toast.error('Erro ao carregar movimentações');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (movimentacao) => {
    setEditingMovimentacao(movimentacao);
    setFormData({
      tipo: movimentacao.tipo,
      pessoa_id: movimentacao.pessoa_id,
      titulo: movimentacao.titulo,
      valor_original: movimentacao.valor_original,
      banco: movimentacao.banco || '',
      forma_pagamento: movimentacao.forma_pagamento,
      data: movimentacao.vencimento,
      descricao: movimentacao.descricao || ''
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return; // Prevenir duplo clique
    
    setSaving(true);
    try {
      const dataToSubmit = {
        pessoa_id: formData.pessoa_id || 'movimentacao-manual',
        tipo: formData.tipo,
        categoria_id: 'movimentacao-manual',
        titulo: formData.titulo,
        descricao: formData.descricao,
        valor_original: parseFloat(formData.valor_original),
        desconto: 0,
        acrescimo: 0,
        emissao: formData.data,
        vencimento: formData.data,
        forma_pagamento: formData.forma_pagamento,
        banco: formData.banco
      };

      if (editingMovimentacao) {
        await api.put(`/financeiros/${editingMovimentacao.id}`, dataToSubmit);
        toast.success('Movimentação atualizada!');
      } else {
        const response = await api.post('/financeiros', dataToSubmit);
        
        // Marcar como pago imediatamente usando o ID retornado
        const novoFinanceiroId = response.data.id;
        await api.post(`/financeiros/${novoFinanceiroId}/quitar`);
        
        toast.success('Movimentação registrada!');
      }
      
      setDialogOpen(false);
      resetForm();
      loadMovimentacoes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setEditingMovimentacao(null);
    setFormData({
      tipo: 'receber',
      pessoa_id: '',
      titulo: '',
      valor_original: '',
      banco: '',
      forma_pagamento: 'PIX',
      data: new Date().toISOString().split('T')[0],
      descricao: ''
    });
  };

  const handleRecibo = async (id) => {
    const toastId = toast.loading('Gerando recibo...');
    try {
      const { data: blob } = await api.get(`/financeiros/${id}/recibo`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Recibo_BoraCar_${id.slice(0, 8).toUpperCase()}.pdf`;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => { document.body.removeChild(link); window.URL.revokeObjectURL(url); }, 100);
      toast.success('Recibo gerado!', { id: toastId });
    } catch (error) {
      const msg = error.response?.status === 404 ? 'Recibo não encontrado' : 'Erro ao gerar recibo';
      toast.error(msg, { id: toastId });
    }
  };

  const handleExcluir = async (id) => {
    try {
      await api.delete(`/financeiros/${id}/excluir`);
      toast.success('Movimentação excluída!');
      loadMovimentacoes();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const handleSalvarCadastro = async (e) => {
    e.preventDefault();
    if (saving) return; // Prevenir duplo clique
    
    setSaving(true);
    try {
      const dataToSend = {
        tipo: formCadastro.tipo,
        nome_razao: formCadastro.nome_razao,
        fantasia: '',
        cpf_cnpj: formCadastro.cpf_cnpj.replace(/\D/g, ''),
        rg_ie: '',
        nasc_abertura: '',
        email: formCadastro.email || '',
        telefone: '',
        celular: formCadastro.celular.replace(/\D/g, ''),
        endereco: { logradouro: '', numero: '', bairro: '', cidade: '', estado: '', cep: '' },
        cliente: formCadastro.cliente,
        fornecedor: formCadastro.fornecedor,
        motorista: formCadastro.motorista,
        funcionario: formCadastro.funcionario,
        chave_pix: '',
        observacoes: ''
      };
      
      await api.post('/pessoas', dataToSend);
      toast.success('Cadastro criado! Recarregando lista...');
      setDialogCadastro(false);
      setFormCadastro({
        tipo: 'PF',
        nome_razao: '',
        cpf_cnpj: '',
        email: '',
        celular: '',
        cliente: false,
        fornecedor: false,
        motorista: false,
        funcionario: false
      });
      loadPessoas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar cadastro');
    } finally {
      setSaving(false);
    }
  };

  const handleExportPDF = async () => {
    const toastId = toast.loading('Gerando PDF...');
    try {
      const params = new URLSearchParams();
      if (filtros.banco) params.append('banco', filtros.banco);
      if (filtros.tipo) params.append('tipo', filtros.tipo);
      if (filtros.data_inicio) params.append('data_inicio', filtros.data_inicio);
      if (filtros.data_fim) params.append('data_fim', filtros.data_fim);

      const { data: blob } = await api.get(`/entradas-saidas/export/pdf?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const di = filtros.data_inicio ? filtros.data_inicio.split('-').reverse().join('') : '';
      const df = filtros.data_fim ? filtros.data_fim.split('-').reverse().join('') : '';
      const periodoES = di && df ? `_${di}_${df}` : '';
      link.download = `EntradasSaidas_BoraCar${periodoES}.pdf`;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => { document.body.removeChild(link); window.URL.revokeObjectURL(url); }, 100);
      toast.success('PDF exportado!', { id: toastId });
    } catch (error) {
      const msg = error.response?.status === 404 ? 'Nenhum dado encontrado para exportar' : 'Erro ao exportar PDF';
      toast.error(msg, { id: toastId });
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const formatDate = (dateStr) => {
    // Evitar problema de timezone: tratar como data local
    const [year, month, day] = dateStr.split('T')[0].split('-');
    return new Date(year, month - 1, day).toLocaleDateString('pt-BR');
  };

  const totalEntradas = movimentacoes
    .filter(m => m.tipo === 'receber')
    .reduce((sum, m) => sum + m.valor_liquido, 0);

  const totalSaidas = movimentacoes
    .filter(m => m.tipo === 'pagar')
    .reduce((sum, m) => sum + m.valor_liquido, 0);

  const saldo = totalEntradas - totalSaidas;

  // Filtrar e ordenar
  let movsProcessadas = movimentacoes;
  
  // Busca por nome ou descrição (sem acentos)
  if (searchTerm) {
    movsProcessadas = movsProcessadas.filter(m =>
      searchWithoutAccents(m.titulo, searchTerm) ||
      searchWithoutAccents(m.pessoa_nome, searchTerm)
    );
  }
  
  // Ordenação hierárquica
  movsProcessadas = [...movsProcessadas].sort((a, b) => {
    if (orderBy === 'data') {
      // Ordenação hierárquica: 
      // 1º por data de vencimento (mais recente primeiro)
      // 2º por data de criação/inserção (último inserido primeiro)
      const vencimentoA = new Date(a.vencimento);
      const vencimentoB = new Date(b.vencimento);
      
      // Comparar vencimentos
      if (orderDir === 'desc') {
        // Se vencimentos diferentes, ordenar por vencimento decrescente
        if (vencimentoA.getTime() !== vencimentoB.getTime()) {
          return vencimentoB.getTime() - vencimentoA.getTime();
        }
      } else {
        // Se vencimentos diferentes, ordenar por vencimento crescente
        if (vencimentoA.getTime() !== vencimentoB.getTime()) {
          return vencimentoA.getTime() - vencimentoB.getTime();
        }
      }
      
      // Se vencimentos iguais, ordenar por criado_em (sempre decrescente - último inserido primeiro)
      const criadoA = new Date(a.criado_em || a.emissao);
      const criadoB = new Date(b.criado_em || b.emissao);
      return criadoB.getTime() - criadoA.getTime();
      
    } else {
      // Outras ordenações (nome, descrição, valor)
      let compareA, compareB;
      
      if (orderBy === 'nome') {
        compareA = a.pessoa_nome.toLowerCase();
        compareB = b.pessoa_nome.toLowerCase();
      } else if (orderBy === 'descricao') {
        compareA = a.titulo.toLowerCase();
        compareB = b.titulo.toLowerCase();
      } else if (orderBy === 'valor') {
        compareA = a.valor_liquido;
        compareB = b.valor_liquido;
      }
      
      if (orderDir === 'asc') {
        return compareA > compareB ? 1 : -1;
      } else {
        return compareA < compareB ? 1 : -1;
      }
    }
  });

  return (
    <div className="space-y-6 animate-slide-up" data-testid="entradas-saidas-page">
      <ConfirmModal
        isOpen={confirmDelete.isOpen}
        onClose={() => setConfirmDelete({ isOpen: false, id: null })}
        onConfirm={() => handleExcluir(confirmDelete.id)}
        title="Excluir Movimentação"
        message="Deseja realmente excluir esta movimentação? Esta ação não pode ser revertida."
        confirmText="Sim, Excluir"
        cancelText="Cancelar"
      />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Entradas e Saídas</h1>
          <p className="text-slate-600">Gerencie movimentações financeiras diárias</p>
        </div>
        <div className="flex space-x-2">
          <Button 
            onClick={() => setDialogCadastro(true)}
            variant="outline"
            className="border-green-600 text-green-600 hover:bg-green-50"
          >
            <Plus className="w-4 h-4 mr-2" />
            Novo Cadastro
          </Button>
          <Button 
            onClick={handleExportPDF}
            variant="outline"
            className="border-red-600 text-red-600 hover:bg-red-50"
          >
            <FileText className="w-4 h-4 mr-2" />
            Exportar PDF
          </Button>
          <Button 
            data-testid="add-movimentacao-button" 
            onClick={() => { resetForm(); setDialogOpen(true); }} 
            className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nova Movimentação
          </Button>
        </div>
      </div>

      {/* Modal */}
      {dialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setDialogOpen(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto m-4" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-900">{editingMovimentacao ? 'Editar Movimentação' : 'Nova Movimentação'}</h2>
              <button onClick={() => setDialogOpen(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tipo</Label>
                  <select
                    data-testid="mov-tipo-select"
                    value={formData.tipo}
                    onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                    className="input-field"
                  >
                    <option value="receber">Entrada (Recebimento)</option>
                    <option value="pagar">Saída (Pagamento)</option>
                  </select>
                </div>
                <div>
                  <Label>Data</Label>
                  <Input
                    type="date"
                    value={formData.data}
                    onChange={(e) => setFormData({ ...formData, data: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div>
                <SearchableSelect
                  label={formData.tipo === 'receber' ? 'Cliente/Motorista (quem pagou)' : 'Fornecedor/Motorista (quem recebeu)'}
                  placeholder="Digite para buscar ou selecionar..."
                  value={formData.pessoa_id}
                  onChange={(value) => setFormData({ ...formData, pessoa_id: value })}
                  options={[
                    ...pessoas
                      .filter(p => p.ativo)
                      .map(p => ({
                        value: p.id,
                        label: p.nome_razao,
                        subtitle: `${p.cpf_cnpj}${p.cliente ? ' [Cliente]' : ''}${p.fornecedor ? ' [Fornecedor]' : ''}${p.motorista ? ' [Motorista]' : ''}`
                      })),
                    { value: 'movimentacao-manual', label: '--- Sem pessoa (Movimentação Manual) ---', subtitle: '' }
                  ]}
                />
                <p className="text-xs text-slate-500 mt-1">
                  Digite para buscar ou selecione da lista
                </p>
              </div>

              <div>
                <Label>Descrição</Label>
                <Input
                  data-testid="mov-titulo-input"
                  value={formData.titulo}
                  onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                  placeholder="Ex: Venda de produto, Pagamento fornecedor..."
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Valor</Label>
                  <Input
                    data-testid="mov-valor-input"
                    type="number"
                    step="0.01"
                    value={formData.valor_original}
                    onChange={(e) => setFormData({ ...formData, valor_original: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Banco</Label>
                  <select
                    data-testid="mov-banco-select"
                    value={formData.banco}
                    onChange={(e) => setFormData({ ...formData, banco: e.target.value })}
                    className="input-field"
                    required
                  >
                    <option value="">Selecione...</option>
                    {bancos.map(b => (
                      <option key={b.id} value={b.nome}>{b.nome}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <Label>Forma de Pagamento</Label>
                <select
                  value={formData.forma_pagamento}
                  onChange={(e) => setFormData({ ...formData, forma_pagamento: e.target.value })}
                  className="input-field"
                  required
                >
                  <option value="Dinheiro">Dinheiro</option>
                  <option value="Cartão de Crédito">Cartão de Crédito</option>
                  <option value="Cartão de Débito">Cartão de Débito</option>
                  <option value="PIX">PIX</option>
                  <option value="Boleto">Boleto</option>
                  <option value="Transferência">Transferência</option>
                </select>
              </div>

              <div>
                <Label>Observações</Label>
                <textarea
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  className="input-field"
                  rows="3"
                />
              </div>

              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
                  Cancelar
                </Button>
                <Button data-testid="save-mov-button" type="submit" disabled={saving} className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700">
                  {saving ? 'Salvando...' : 'Registrar'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Novo Cadastro Rápido */}
      {dialogCadastro && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setDialogCadastro(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full m-4" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-4 rounded-t-xl flex items-center justify-between">
              <h2 className="text-xl font-bold">Cadastro Rápido de Pessoa</h2>
              <button onClick={() => setDialogCadastro(false)} className="p-2 hover:bg-white/10 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSalvarCadastro} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tipo</Label>
                  <select
                    value={formCadastro.tipo}
                    onChange={(e) => setFormCadastro({ ...formCadastro, tipo: e.target.value })}
                    className="input-field"
                  >
                    <option value="PF">Pessoa Física</option>
                    <option value="PJ">Pessoa Jurídica</option>
                  </select>
                </div>
                <div>
                  <Label>{formCadastro.tipo === 'PF' ? 'Nome Completo' : 'Razão Social'}</Label>
                  <Input
                    value={formCadastro.nome_razao}
                    onChange={(e) => setFormCadastro({ ...formCadastro, nome_razao: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div>
                <Label>{formCadastro.tipo === 'PF' ? 'CPF' : 'CNPJ'}</Label>
                <Input
                  value={formCadastro.cpf_cnpj}
                  onChange={(e) => setFormCadastro({ ...formCadastro, cpf_cnpj: e.target.value })}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formCadastro.email}
                    onChange={(e) => setFormCadastro({ ...formCadastro, email: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Celular</Label>
                  <Input
                    value={formCadastro.celular}
                    onChange={(e) => setFormCadastro({ ...formCadastro, celular: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <Label className="mb-2 block">Tipo de Cadastro</Label>
                <div className="flex items-center flex-wrap gap-4">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="cad-cliente"
                      checked={formCadastro.cliente}
                      onChange={(e) => setFormCadastro({ ...formCadastro, cliente: e.target.checked })}
                      className="w-4 h-4 rounded border-slate-300"
                    />
                    <Label htmlFor="cad-cliente">Cliente</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="cad-fornecedor"
                      checked={formCadastro.fornecedor}
                      onChange={(e) => setFormCadastro({ ...formCadastro, fornecedor: e.target.checked })}
                      className="w-4 h-4 rounded border-slate-300"
                    />
                    <Label htmlFor="cad-fornecedor">Fornecedor</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="cad-motorista"
                      checked={formCadastro.motorista}
                      onChange={(e) => setFormCadastro({ ...formCadastro, motorista: e.target.checked })}
                      className="w-4 h-4 rounded border-slate-300"
                    />
                    <Label htmlFor="cad-motorista">Motorista</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="cad-funcionario"
                      checked={formCadastro.funcionario}
                      onChange={(e) => setFormCadastro({ ...formCadastro, funcionario: e.target.checked })}
                      className="w-4 h-4 rounded border-slate-300"
                    />
                    <Label htmlFor="cad-funcionario">Funcionário</Label>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setDialogCadastro(false)} disabled={saving}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={saving} className="bg-gradient-to-r from-green-600 to-emerald-600">
                  {saving ? 'Salvando...' : 'Salvar Cadastro'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Filtros e Busca */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div className="md:col-span-2">
            <Label className="text-xs">Buscar</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                data-testid="search-movimentacoes-input"
                placeholder="Nome ou descrição..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
          
          <div>
            <Label className="text-xs">Ordenar por</Label>
            <select
              value={orderBy}
              onChange={(e) => setOrderBy(e.target.value)}
              className="input-field"
            >
              <option value="data">Data</option>
              <option value="nome">Nome</option>
              <option value="descricao">Descrição</option>
              <option value="valor">Valor</option>
            </select>
          </div>
          
          <div>
            <Label className="text-xs">Direção</Label>
            <select
              value={orderDir}
              onChange={(e) => setOrderDir(e.target.value)}
              className="input-field"
            >
              <option value="desc">Decrescente</option>
              <option value="asc">Crescente</option>
            </select>
          </div>
          
          <div>
            <Label className="text-xs">Data Início</Label>
            <Input
              type="date"
              value={filtros.data_inicio}
              onChange={(e) => setFiltros({ ...filtros, data_inicio: e.target.value })}
            />
          </div>
          <div>
            <Label className="text-xs">Data Fim</Label>
            <Input
              type="date"
              value={filtros.data_fim}
              onChange={(e) => setFiltros({ ...filtros, data_fim: e.target.value })}
            />
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
          <div>
            <Label className="text-xs">Banco</Label>
            <select
              value={filtros.banco}
              onChange={(e) => setFiltros({ ...filtros, banco: e.target.value })}
              className="input-field"
            >
              <option value="">Todos</option>
              {bancos.map(b => (
                <option key={b.id} value={b.nome}>{b.nome}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs">Tipo</Label>
            <select
              value={filtros.tipo}
              onChange={(e) => setFiltros({ ...filtros, tipo: e.target.value })}
              className="input-field"
            >
              <option value="">Todos</option>
              <option value="receber">Entradas</option>
              <option value="pagar">Saídas</option>
            </select>
          </div>
          <div className="md:col-span-2 flex items-end">
            <Button onClick={loadMovimentacoes} className="w-full bg-gradient-to-r from-red-600 to-orange-600">
              <Search className="w-4 h-4 mr-2" />
              Buscar
            </Button>
          </div>
        </div>
      </div>

      {/* Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-green-50 to-white border border-green-200 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600 mb-1">Total Entradas</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(totalEntradas)}</p>
            </div>
            <ArrowUpCircle className="w-10 h-10 text-green-600" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-red-50 to-white border border-red-200 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600 mb-1">Total Saídas</p>
              <p className="text-2xl font-bold text-red-600">{formatCurrency(totalSaidas)}</p>
            </div>
            <ArrowDownCircle className="w-10 h-10 text-red-600" />
          </div>
        </div>
        <div className={`bg-gradient-to-br ${saldo >= 0 ? 'from-blue-50 to-white border-blue-200' : 'from-orange-50 to-white border-orange-200'} rounded-xl p-6`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600 mb-1">Saldo</p>
              <p className={`text-2xl font-bold ${saldo >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                {formatCurrency(saldo)}
              </p>
            </div>
            <Building2 className={`w-10 h-10 ${saldo >= 0 ? 'text-blue-600' : 'text-orange-600'}`} />
          </div>
        </div>
      </div>

      {/* Lista de Movimentações */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Tipo</th>
                  <th>Cliente/Fornecedor</th>
                  <th>Descrição</th>
                  <th>Banco</th>
                  <th>Forma Pgto</th>
                  <th>Valor</th>
                  <th className="text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {movsProcessadas.map((mov) => (
                  <tr key={mov.id}>
                    <td>
                      <div className="flex items-center space-x-2">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        <span>{formatDate(mov.vencimento)}</span>
                      </div>
                    </td>
                    <td>
                      {mov.tipo === 'receber' ? (
                        <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                          ENTRADA
                        </span>
                      ) : (
                        <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold">
                          SAÍDA
                        </span>
                      )}
                    </td>
                    <td className="font-semibold text-slate-900">{mov.pessoa_nome}</td>
                    <td>{mov.titulo}</td>
                    <td>
                      <span className="px-2 py-1 bg-slate-100 text-slate-700 rounded text-xs font-medium">
                        {mov.banco || 'Não informado'}
                      </span>
                    </td>
                    <td className="text-sm">{mov.forma_pagamento}</td>
                    <td>
                      {mov.tipo === 'receber' ? (
                        <span className="font-bold text-green-600">
                          + {formatCurrency(mov.valor_liquido)}
                        </span>
                      ) : (
                        <span className="font-bold text-red-600">
                          - {formatCurrency(mov.valor_liquido)}
                        </span>
                      )}
                    </td>
                    <td className="text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <Button
                          data-testid={`recibo-mov-${mov.id}`}
                          size="sm"
                          variant="outline"
                          className="text-purple-600 hover:bg-purple-50"
                          onClick={() => handleRecibo(mov.id)}
                          title="Baixar Recibo"
                        >
                          <FileText className="w-4 h-4" />
                        </Button>
                        <Button
                          data-testid={`edit-mov-${mov.id}`}
                          size="sm"
                          variant="outline"
                          onClick={() => handleEdit(mov)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          data-testid={`delete-mov-${mov.id}`}
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => setConfirmDelete({ isOpen: true, id: mov.id })}
                          title="Excluir"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default EntradasSaidas;
